import { returnTrip } from '../../shell/returnTo';
import { demoForIdea } from '../../journey/demos';

/**
 * The sentence an ad's visitor typed, read back out of where the journey was headed.
 *
 * The guard hands sign-up the arrival address (`from`, shell/RequireAuth.tsx), and an ad's
 * address is /new/idea?idea=<their sentence>. returnTrip is what the sign-in at the end of the
 * trip uses to carry that sentence on, so reading it the same way here means the page can only
 * ever show an idea the trip will deliver — and never one from an address on another site,
 * which returnTrip refuses before it looks at the query.
 */
export function ideaFrom(from: unknown): string | null {
  const idea = returnTrip(from).state?.idea.trim();
  return idea ? idea : null;
}

/**
 * Their idea, quoted back to them. On Sign up it is the reason to finish the form; on the code
 * screen, after a detour through their inbox, it is the reminder of why they are there.
 *
 * Shown as text, never as markup: the sentence came from a URL anyone can write.
 */
export function IdeaQuote({ idea, appName }: { idea: string; appName?: string | null }) {
  const quote = quoteFor(idea, appName);
  return (
    <div className="card clay-card auth-idea">
      <p className="type-micro auth-idea-label">{quote.name ? `Your app: ${quote.name}` : 'Your app:'}</p>
      <p className="type-heading auth-idea-text">{`“${quote.text}”`}</p>
    </div>
  );
}

/** About two lines of the quote at a phone's width: past it, the words are cut at a word. */
export const QUOTE_MAX = 64;

/**
 * What the quote says (V6 polish, 2026-10-14). A build from an ad's demo is named (`appName`, the
 * plan's) and quoted in the ad's own short sentence, not the long prompt the demo was made from.
 * Anything longer than QUOTE_MAX is cut at the last whole word, with "…": a two-line clamp alone
 * cut "Before" to "Befor…".
 */
export function quoteFor(idea: string, appName?: string | null): { name: string | null; text: string } {
  const demo = demoForIdea(idea);
  const words = (demo ? demo.sentence : idea).replace(/\s+/g, ' ').trim();
  return { name: demo ? appName?.trim() || null : null, text: cutAtWord(words, QUOTE_MAX) };
}

function cutAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  // The last space at or before `max`: everything before it is whole words.
  const space = text.lastIndexOf(' ', max);
  const kept = (space > 0 ? text.slice(0, space) : text.slice(0, max)).replace(/[\s,;:.\u2014\u2013-]+$/, '');
  return `${kept}…`;
}

/**
 * A finished site, drawn in CSS: the wedding RSVP, the demo the ads lead with.
 *
 * Drawn rather than an image so it costs no request on a phone that came from an ad on a
 * slow connection, and needs no host but this one. It is an illustration and says so in its
 * name; the shapes stand in for text a real site would carry.
 */
export function ExampleSite() {
  return (
    <div className="example-site" role="img" aria-label="Example: a wedding website with an RSVP, the kind Saddle builds">
      <div className="example-site-hero" aria-hidden="true">
        <span className="example-site-kicker">June 11</span>
        <span className="example-site-title">Maya &amp; Sam</span>
      </div>
      <div className="example-site-body" aria-hidden="true">
        <span className="example-site-line" />
        <span className="example-site-line is-short" />
        <span className="example-site-rsvp">RSVP</span>
        <span className="example-site-row" />
        <span className="example-site-row" />
      </div>
    </div>
  );
}
